import type { ReactNode } from 'react';

/*
 * The `_` cursor loader (docs/brand.md → Brand motifs): a primary block that blinks at
 * 1s steps(2), shown steadily under prefers-reduced-motion (`.cursor-blink` in index.css).
 */

const SIZE_CLASS = {
  inherit: '',
  sm: 'text-sm',
  md: 'text-2xl',
  lg: 'text-4xl',
} as const;

interface CursorProps {
  /** Font size the 0.6em × 1em block is measured against. `inherit` follows the text around it. */
  size?: keyof typeof SIZE_CLASS;
  /** Announces the cursor as a loading status. Omit when a visible label already says it. */
  label?: string;
  className?: string;
}

export function Cursor({ size = 'inherit', label, className = '' }: CursorProps) {
  return (
    <span
      className={`cursor-blink inline-block w-[0.6em] h-[1em] align-[-0.125em] bg-primary ${SIZE_CLASS[size]} ${className}`}
      {...(label ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true })}
    />
  );
}

interface LoadingProps {
  /** Visible text under the cursor, e.g. "Loading requests…". */
  label?: string;
  size?: 'md' | 'lg';
  className?: string;
}

/** A page- or section-level loading state: a centred cursor with an optional Muted label. */
export function Loading({ label, size = 'md', className = '' }: LoadingProps) {
  return (
    <div
      role="status"
      aria-label={label ? undefined : 'Loading'}
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
    >
      <Cursor size={size} />
      {label && <p className="text-sm text-base-content/70">{label}</p>}
    </div>
  );
}

/**
 * A button label in its loading state: the label plus a blinking `_` (`Saving_`). The
 * underscore takes the button's text colour, so it reads on any button variant.
 */
export function LoadingLabel({ children }: { children: ReactNode }) {
  return (
    <span>
      {children}
      <span aria-hidden="true" className="cursor-blink">_</span>
    </span>
  );
}
